import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  Home, 
  LayoutGrid, 
  Trophy, 
  Vote, 
  Gift, 
  User, 
  Bell, 
  LogOut, 
  Menu, 
  X,
  Settings,
  Gavel,
  PlusCircle,
  CreditCard
} from 'lucide-react';

function Navigation() {
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchUser();
    fetchNotifications();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      }
    } catch (error) {
      console.error('Not logged in');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/notifications`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.filter(n => !n.is_read).length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout failed');
    }
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/feed', label: 'Feed', icon: LayoutGrid },
    { path: '/voting', label: 'Vote', icon: Vote },
    { path: '/earn-votes', label: 'Earn Votes', icon: Gift },
    { path: '/buy-votes', label: 'Buy Votes', icon: CreditCard },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  if (!user) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/feed')} className="flex items-center gap-2">
              <img src="/logo.png" alt="Tsarona Media Africa" className="h-10" />
              <span className="md:hidden text-white font-bold text-lg" style={{ fontFamily: 'Archivo, sans-serif' }}>Tsarona Voting</span>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate('/login')} className="rounded-full">
                Login
              </Button>
              <Button onClick={() => navigate('/join-talent-show')} className="bg-primary rounded-full">
                Register
              </Button>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button onClick={() => navigate(`/profile/${user.user_id}`)} className="flex items-center gap-2">
              <img src="/logo.png" alt="Tsarona Media Africa" className="h-10" />
              <span className="md:hidden text-white font-bold text-lg" style={{ fontFamily: 'Archivo, sans-serif' }}>Tsarona Voting</span>
            </button>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => navigate(item.path)}
                  className={`rounded-full px-4 ${isActive(item.path) ? 'bg-primary/20 text-primary' : ''}`}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Create Post Button */}
              {user.is_artist && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/create-post')}
                  className="rounded-full hidden sm:flex"
                >
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Post
                </Button>
              )}

              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/notifications')}
                className="relative rounded-full"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>

              {/* Admin/Judge Links */}
              {user.is_admin && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/admin')}
                  className={`rounded-full hidden sm:flex ${isActive('/admin') ? 'bg-primary/20 text-primary' : ''}`}
                >
                  <Settings className="h-5 w-5" />
                </Button>
              )}

              {user.is_judge && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/judging-panel')}
                  className={`rounded-full hidden sm:flex ${isActive('/judging-panel') ? 'bg-primary/20 text-primary' : ''}`}
                >
                  <Gavel className="h-5 w-5" />
                </Button>
              )}

              {/* Profile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/profile/${user.user_id}`)}
                className="rounded-full"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <User className="h-5 w-5" />
                )}
              </Button>

              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden rounded-full"
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>

              {/* Desktop Logout */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="rounded-full hidden md:flex text-muted-foreground hover:text-red-400"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  onClick={() => { navigate(item.path); setIsMenuOpen(false); }}
                  className={`w-full justify-start rounded-lg ${isActive(item.path) ? 'bg-primary/20 text-primary' : ''}`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.label}
                </Button>
              ))}
              
              {user.is_artist && (
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/create-post'); setIsMenuOpen(false); }}
                  className="w-full justify-start rounded-lg"
                >
                  <PlusCircle className="h-5 w-5 mr-3" />
                  Create Post
                </Button>
              )}

              {user.is_admin && (
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/admin'); setIsMenuOpen(false); }}
                  className={`w-full justify-start rounded-lg ${isActive('/admin') ? 'bg-primary/20 text-primary' : ''}`}
                >
                  <Settings className="h-5 w-5 mr-3" />
                  Admin Panel
                </Button>
              )}

              {user.is_judge && (
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/judging-panel'); setIsMenuOpen(false); }}
                  className={`w-full justify-start rounded-lg ${isActive('/judging-panel') ? 'bg-primary/20 text-primary' : ''}`}
                >
                  <Gavel className="h-5 w-5 mr-3" />
                  Judging Panel
                </Button>
              )}

              <Button
                variant="ghost"
                onClick={() => { navigate(`/profile/${user.user_id}`); setIsMenuOpen(false); }}
                className="w-full justify-start rounded-lg"
              >
                <User className="h-5 w-5 mr-3" />
                My Profile
              </Button>

              <div className="pt-2 border-t border-white/10">
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="w-full justify-start rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Spacer to prevent content from going under nav */}
      <div className="h-16" />
    </>
  );
}

export default Navigation;
